'use client';

import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { useEffect, useRef, useState, type ComponentType } from 'react';
import type { ParticipantAccount } from '@/lib/participant-auth/sessions';
import type { BrandPreviewId } from '@/lib/brand-preview/shared';
import { MyHyroxLockup } from '@/components/brand-preview/HyroxWordmark';
import { MyFitventLockup } from '@/components/design/FitventMark';
import { HubAvatar, HubIcons, HubSkeleton, initialsOf } from './hub-ui';
import { hubBrandSkinCss } from './hub-skin';
import { claimStaleLayoutRefresh, clearStaleLayoutRefresh, tabStore } from './stale-layout-refresh';
import { SESSION_ENDED_KEY, isProtectedHubPath, sessionEndedHref } from '@/lib/participant-auth/session-ended';

/**
 * The athlete hub's frame, from the Athlete Hub handoff.
 *
 * Desktop and tablet (768px and up): a 216px rail on the left with the
 * myfitvent lockup, the five sections and the athlete at the foot. Phone: no
 * rail, a 62px tab bar along the bottom (plus the home-indicator inset).
 *
 * The content column is the same for every screen inside it: centred, at most
 * 960px wide, padded 30px 36px on desktop and 18px 16px on a phone, with room
 * left at the foot for the tab bar. Screens set nothing of their own around it.
 *
 * Rail and tab bar switch with Tailwind's `md:` classes and NO inline display,
 * so the right one is painted on the first frame with no client measurement.
 */

const NAV_ITEMS: Array<{ href: string; label: string; icon: ComponentType<{ size?: number }> }> = [
  { href: '/hub/home', label: 'Home', icon: HubIcons.Home },
  { href: '/hub/races', label: 'Races', icon: HubIcons.Races },
  { href: '/hub/discover', label: 'Discover', icon: HubIcons.Discover },
  { href: '/hub/stats', label: 'Stats', icon: HubIcons.Stats },
  { href: '/hub/profile', label: 'Profile', icon: HubIcons.Profile },
];

interface HubShellProps {
  /** Only the identity the rail shows. A full account still fits. */
  participant: Pick<ParticipantAccount, 'firstName' | 'lastName' | 'username' | 'avatarUrl'> | null;
  /** The layout found a session cookie with no session behind it. */
  sessionEnded?: boolean;
  hasUnreadResults?: boolean;
  brandPreview?: BrandPreviewId | null;
  children: React.ReactNode;
}

export function HubShell({ participant, sessionEnded = false, hasUnreadResults = false, brandPreview = null, children }: HubShellProps) {
  const pathname = usePathname();
  const router = useRouter();
  const refreshed = useRef(false);
  const leftForSignIn = useRef(false);
  // A dead session on a page that needs one: hold the page back (its loads
  // would only fail) while the athlete is sent to sign in, unless the trip was
  // refused (taken once already this minute), when the page shows as before.
  const [tripRefused, setTripRefused] = useState(false);
  const leaving = sessionEnded && !participant && isProtectedHubPath(pathname) && !tripRefused;

  // Signing in happens on /hub/login, inside this same layout, and then moves
  // to the hub with a client-side navigation. A layout is not re-rendered on
  // a client navigation, so the account it read (none, on the login page)
  // stayed: home greeted "athlete" and the rail had no name until a full
  // reload. Any hub page other than login only renders with a session cookie
  // (middleware sends everyone else to login), so no account here means the
  // layout is stale: refresh it once to read the session. Once per tab per
  // minute, not per mount: a failed refresh reloads the page, which mounts a
  // new shell (stale-layout-refresh.ts).
  useEffect(() => {
    // An account, or a sign in about to start: the next stale layout (the one
    // right after signing in) gets its refresh straight away.
    if (participant || pathname === '/hub/login') {
      clearStaleLayoutRefresh(tabStore());
      clearStaleLayoutRefresh(tabStore(), SESSION_ENDED_KEY);
      return;
    }
    if (!isProtectedHubPath(pathname)) return;
    // The cookie's session has gone: to sign in, via a server check that
    // clears the dead cookie (or comes straight back if the layout was only
    // stale). Once a minute per tab, so it can never loop; if it was refused,
    // the page shows as it did before.
    if (sessionEnded) {
      if (leftForSignIn.current) return;
      leftForSignIn.current = true;
      if (!claimStaleLayoutRefresh(tabStore(), Date.now(), SESSION_ENDED_KEY)) {
        // sessionStorage is only readable after mount, so this cannot be
        // known during render: one extra render, once.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTripRefused(true);
        return;
      }
      window.location.replace(sessionEndedHref(pathname, window.location.search));
      return;
    }
    if (refreshed.current) return;
    refreshed.current = true;
    if (!claimStaleLayoutRefresh(tabStore(), Date.now())) return;
    router.refresh();
  }, [participant, pathname, router, sessionEnded]);

  // Don't show shell on login page
  if (pathname === '/hub/login') {
    return <>{children}</>;
  }

  const initials = participant ? initialsOf(participant.firstName, participant.lastName) : '?';
  const skinCss = hubBrandSkinCss(brandPreview);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--fvd-canvas)',
        color: 'var(--fvd-ink)',
        fontFamily: 'var(--fvd-font-body)',
        fontVariantNumeric: 'tabular-nums',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      {skinCss && <style dangerouslySetInnerHTML={{ __html: skinCss }} />}

      {/* Rail: tablet and desktop */}
      <aside
        className="hidden md:flex"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 40,
          width: 216,
          flexDirection: 'column',
          background: 'var(--fvd-rail)',
          borderRight: '1px solid var(--fvd-hairline)',
        }}
      >
        <div style={{ padding: '16px 14px 12px' }}>
          <Link
            href="/hub/home"
            aria-label={brandPreview === 'hyrox' ? 'myHYROX home' : 'MyFitvent home'}
            style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--fvd-ink)', textDecoration: 'none', minHeight: 28 }}
          >
            {brandPreview === 'hyrox' ? <MyHyroxLockup size={13} /> : <MyFitventLockup markSize={17} />}
          </Link>
        </div>

        <nav aria-label="Hub" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {NAV_ITEMS.map((item) => {
            const on = pathname.startsWith(item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={on ? 'page' : undefined}
                className="fvd-hoverable"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  minHeight: 36,
                  borderRadius: 8,
                  fontSize: 13.5,
                  textDecoration: 'none',
                  background: on ? 'var(--fvd-selected-fill)' : 'transparent',
                  color: on ? 'var(--fvd-ink)' : 'var(--fvd-ink-secondary)',
                  fontWeight: on ? 600 : 400,
                  position: 'relative',
                }}
              >
                <span style={{ flex: 'none', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', color: on ? 'var(--fvd-teal-text)' : 'var(--fvd-ink-muted)' }}>
                  <Icon size={18} />
                </span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.href === '/hub/races' && hasUnreadResults && (
                  <span aria-label="New results" style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--fvd-amber)' }} />
                )}
              </Link>
            );
          })}
        </nav>

        {participant && (
          <div style={{ flex: 'none', padding: 10, borderTop: '1px solid var(--fvd-hairline-light)' }}>
            <Link
              href="/hub/profile"
              className="fvd-hoverable"
              style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 8px', borderRadius: 8, textDecoration: 'none', color: 'var(--fvd-ink)' }}
            >
              <HubAvatar url={participant.avatarUrl} initials={initials} size={26} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {[participant.firstName, participant.lastName].filter(Boolean).join(' ') || 'Your profile'}
                </span>
                {participant.username && (
                  <span style={{ display: 'block', fontFamily: 'var(--fvd-font-mono)', fontSize: 10.5, color: 'var(--fvd-ink-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    @{participant.username}
                  </span>
                )}
              </span>
            </Link>
          </div>
        )}
      </aside>

      {/* Content column */}
      <main className="md:ml-[216px]" style={{ minWidth: 0 }}>
        <div className="px-4 pt-[18px] pb-[calc(env(safe-area-inset-bottom)+96px)] md:px-9 md:pt-[30px] md:pb-20" style={{ maxWidth: 960, margin: '0 auto' }}>
          {leaving ? <SignInHandoffSkeleton /> : children}
        </div>
      </main>

      {/* Tab bar: phone */}
      <nav
        aria-label="Hub"
        className="flex md:hidden"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          alignItems: 'stretch',
          height: 'calc(62px + env(safe-area-inset-bottom, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          borderTop: '1px solid var(--fvd-hairline)',
          background: 'color-mix(in srgb, var(--fvd-canvas) 94%, transparent)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        {NAV_ITEMS.map((item) => {
          const on = pathname.startsWith(item.href);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              href={item.href}
              aria-current={on ? 'page' : undefined}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                minWidth: 44,
                textDecoration: 'none',
                color: on ? 'var(--fvd-teal-text)' : 'var(--fvd-ink-muted)',
                position: 'relative',
              }}
            >
              <span style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={20} />
              </span>
              <span style={{ fontSize: 10.5, fontWeight: on ? 600 : 400 }}>{item.label}</span>
              {item.href === '/hub/races' && hasUnreadResults && (
                <span aria-label="New results" style={{ position: 'absolute', top: 9, left: 'calc(50% + 8px)', width: 7, height: 7, borderRadius: 99, background: 'var(--fvd-amber)' }} />
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

/** A page-shaped placeholder while a dead session hands over to sign in. */
function SignInHandoffSkeleton() {
  return (
    <div aria-hidden="true">
      <HubSkeleton height={28} width="45%" />
      <HubSkeleton height={14} width="30%" style={{ marginTop: 10 }} />
      <HubSkeleton height={120} radius={12} style={{ marginTop: 26 }} />
      <HubSkeleton height={56} radius={12} style={{ marginTop: 14 }} />
      <HubSkeleton height={56} radius={12} style={{ marginTop: 10 }} />
    </div>
  );
}
